const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

export const TABS = [
  {
    key: "team",
    label: "Équipe du club",
    helper: "Bureau, enseignants, démonstration",
  },
  {
    key: "users",
    label: "Utilisateurs",
    helper: "Accès à l'application",
  },
  {
    key: "history",
    label: "Historique",
    helper: "Présentation du club",
  },
];

export const TEAM_CATEGORIES = [
  { value: "Bureau", label: "Bureau" },
  { value: "Enseignant", label: "Enseignant" },
  { value: "Demo", label: "Démonstration" },
];

export const USER_ROLES = [
  { value: 1, label: "Administrateur" },
  { value: 2, label: "Bureau" },
  { value: 3, label: "Enseignant" },
  { value: 4, label: "Démonstration" },
];

export const INITIAL_TEAM_FORM = {
  nom: "",
  prenom: "",
  fonction: "",
  categorie: "",
  photoURL: "",
  imageFile: null,
  position: "",
};

export const INITIAL_USER_FORM = {
  nom: "",
  prenom: "",
  email: "",
  password: "",
  role_id: "",
  photoURL: "",
  imageFile: null,
};

export const INITIAL_HISTORY_FORM = {
  historique_photo_url: "",
  historique_photo2_url: "",
  historique_photo3_url: "",
  historique_title: "",
  historique_content: "",
};

export const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const isDemoUser = (user) => Number(user?.role_id) === 4;

export const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(Number(value || 0));

export const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export const getMediaUrl = (url) => {
  if (!url) return "";
  if (isExternalURL(url)) return url;
  return `${BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

export const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const stripHtml = (html = "") =>
  String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const truncateText = (value = "", limit = 140) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();

  if (text.length <= limit) return text;

  return `${text.slice(0, limit).trim()}...`;
};

export const getFullName = (person) =>
  [person?.prenom, person?.nom].filter(Boolean).join(" ").trim() || "—";

export const getCategoryLabel = (category) => {
  const match = TEAM_CATEGORIES.find((item) => item.value === category);
  return match?.label || category || "Non renseignée";
};

export const getRoleLabel = (roleId) => {
  const match = USER_ROLES.find((role) => Number(role.value) === Number(roleId));
  return match?.label || "Inconnu";
};

export const getCategoryClass = (category) => {
  switch (category) {
    case "Bureau":
      return "bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20";
    case "Enseignant":
      return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";
    case "Demo":
      return "bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";
    default:
      return "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
  }
};

export const getRoleClass = (roleId) => {
  switch (Number(roleId)) {
    case 1:
      return "bg-rose-50 text-rose-700 ring-1 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20";
    case 2:
      return "bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20";
    case 3:
      return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";
    case 4:
      return "bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";
    default:
      return "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
  }
};

export const filterAndSortTeamMembers = (members = [], filters = {}) => {
  const query = normalizeText(filters.query);
  const category = filters.category || "all";

  let result = [...members];

  if (category !== "all") {
    result = result.filter((member) => member.categorie === category);
  }

  if (query) {
    result = result.filter((member) => {
      const haystack = normalizeText(
        [
          member.nom,
          member.prenom,
          member.fonction,
          member.categorie,
        ]
          .filter(Boolean)
          .join(" ")
      );

      return haystack.includes(query);
    });
  }

  result.sort((a, b) => {
    if (filters.sortOrder === "position_desc") {
      return Number(b.position || 0) - Number(a.position || 0);
    }

    if (filters.sortOrder === "name_asc") {
      return normalizeText(getFullName(a)).localeCompare(
        normalizeText(getFullName(b))
      );
    }

    if (filters.sortOrder === "name_desc") {
      return normalizeText(getFullName(b)).localeCompare(
        normalizeText(getFullName(a))
      );
    }

    return Number(a.position || 0) - Number(b.position || 0);
  });

  return result;
};

export const filterAndSortUsers = (users = [], filters = {}) => {
  const query = normalizeText(filters.query);
  const role = filters.role || "all";

  let result = [...users];

  if (role !== "all") {
    result = result.filter((user) => Number(user.role_id) === Number(role));
  }

  if (query) {
    result = result.filter((user) => {
      const haystack = normalizeText(
        [user.nom, user.prenom, user.email, getRoleLabel(user.role_id)]
          .filter(Boolean)
          .join(" ")
      );

      return haystack.includes(query);
    });
  }

  result.sort((a, b) => {
    if (filters.sortOrder === "name_desc") {
      return normalizeText(getFullName(b)).localeCompare(
        normalizeText(getFullName(a))
      );
    }

    if (filters.sortOrder === "role_asc") {
      return Number(a.role_id || 0) - Number(b.role_id || 0);
    }

    if (filters.sortOrder === "role_desc") {
      return Number(b.role_id || 0) - Number(a.role_id || 0);
    }

    return normalizeText(getFullName(a)).localeCompare(
      normalizeText(getFullName(b))
    );
  });

  return result;
};

export const buildClubStats = (teamMembers = [], users = [], settings = null) => {
  const teachers = teamMembers.filter(
    (member) => member.categorie === "Enseignant"
  ).length;

  const board = teamMembers.filter(
    (member) => member.categorie === "Bureau"
  ).length;

  const demo = teamMembers.filter(
    (member) => member.categorie === "Demo"
  ).length;

  const admins = users.filter((user) => Number(user.role_id) === 1).length;

  const historyImages = [
    settings?.historique_photo_url,
    settings?.historique_photo2_url,
    settings?.historique_photo3_url,
  ].filter(Boolean).length;

  return {
    teamTotal: teamMembers.length,
    teachers,
    board,
    demo,
    usersTotal: users.length,
    admins,
    historyImages,
    hasHistoryContent: Boolean(stripHtml(settings?.historique_content)),
  };
};

export const normalizeTeamMemberPayload = (formData) => ({
  ...formData,
  nom: String(formData.nom || "").trim(),
  prenom: String(formData.prenom || "").trim(),
  fonction: String(formData.fonction || "").trim(),
  categorie: String(formData.categorie || "").trim(),
  photoURL: String(formData.photoURL || "").trim(),
  position:
    formData.position === "" ||
    formData.position === null ||
    formData.position === undefined
      ? null
      : Number(formData.position),
});

export const normalizeUserPayload = (formData, mode = "create") => {
  const payload = {
    ...formData,
    nom: String(formData.nom || "").trim(),
    prenom: String(formData.prenom || "").trim(),
    email: String(formData.email || "").trim(),
    role_id: Number(formData.role_id || 0),
    photoURL: String(formData.photoURL || "").trim(),
  };

  if (mode === "edit" && !payload.password) {
    delete payload.password;
  }

  return payload;
};

export const normalizeHistoryPayload = (formData) => ({
  ...formData,
  historique_photo_url: String(formData.historique_photo_url || "").trim(),
  historique_photo2_url: String(formData.historique_photo2_url || "").trim(),
  historique_photo3_url: String(formData.historique_photo3_url || "").trim(),
  historique_title: String(formData.historique_title || "").trim(),
  historique_content: String(formData.historique_content || "").trim(),
});

export const validateTeamMemberForm = (formData) => {
  if (!String(formData.nom || "").trim()) return "Le nom est obligatoire.";
  if (!String(formData.prenom || "").trim()) return "Le prénom est obligatoire.";
  if (!String(formData.fonction || "").trim()) {
    return "La fonction est obligatoire.";
  }
  if (!String(formData.categorie || "").trim()) {
    return "La catégorie est obligatoire.";
  }

  return null;
};

export const validateUserForm = (formData, mode = "create") => {
  if (!String(formData.nom || "").trim()) return "Le nom est obligatoire.";
  if (!String(formData.prenom || "").trim()) return "Le prénom est obligatoire.";

  const email = String(formData.email || "").trim();
  if (!email) return "L'email est obligatoire.";

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return "L'email n'est pas valide.";
  }

  if (mode === "create" && !String(formData.password || "").trim()) {
    return "Le mot de passe est obligatoire à la création.";
  }

  if (!Number(formData.role_id)) return "Le rôle est obligatoire.";

  return null;
};

export const validateHistoryForm = (formData) => {
  if (!String(formData.historique_title || "").trim()) {
    return "Le titre de l'historique est obligatoire.";
  }

  return null;
};

export const buildInitialTeamForm = (member) => {
  if (!member) return INITIAL_TEAM_FORM;

  return {
    ...INITIAL_TEAM_FORM,
    ...member,
    imageFile: null,
  };
};

export const buildInitialUserForm = (user) => {
  if (!user) return INITIAL_USER_FORM;

  return {
    ...INITIAL_USER_FORM,
    ...user,
    password: "",
    imageFile: null,
  };
};

export const buildInitialHistoryForm = (settings) => ({
  ...INITIAL_HISTORY_FORM,
  ...(settings || {}),
});